<template>
  <app-page wide :title="$t('@statisticsPage/title')">
    <errors-list :errorMessage="errorMessage" class="mt-0" />

    <v-expansion-panel v-model="panel" expand>
      <v-expansion-panel-content>
        <template v-slot:header>
          <h3 class="pl-0 py-2">{{ $t('@statisticsPage/upcomingPaymentSection/title') }}</h3>
        </template>

        <v-card flat class="mb-2">
          <v-container fluid grid-list-lg class="py-0">
            <v-layout row align-center justify-center class="py-4" v-if="statLoading">
              <v-flex d-flex sm12 align-content-center justify-center>
                <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
              </v-flex>
            </v-layout>

            <v-layout row wrap v-if="!statLoading">
              <v-flex v-for="(item, idx) in statItems" :key="idx" xs12 sm6 md4 lg3>
                <v-card color="grey lighten-3" flat tile>
                  <v-card-title>
                    <h4>{{ $t(`@statisticsPage/${item.title.replace(/ /g, '')}`) }}</h4>
                  </v-card-title>
                  <v-divider></v-divider>

                  <v-list dense class="transparent">
                    <v-list-tile>
                      <v-list-tile-content class="title">
                        {{ item.count | formatNumber }}
                      </v-list-tile-content>
                      <v-list-tile-content class="align-end">
                        {{
                          $t('@statisticsPage/upcomingPaymentSection/card/numberOfInsurancesSuffix')
                        }}
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-list-tile>
                      <v-list-tile-content class="title">
                        {{ item.sum | formatNumber }}
                      </v-list-tile-content>
                      <v-list-tile-content class="align-end">
                        {{ $t('@statisticsPage/upcomingPaymentSection/card/totalSumSuffix') }}
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-list-tile>
                      <v-list-tile-content></v-list-tile-content>
                      <v-list-tile-content class="align-end grey--text text--darken-1">
                        {{ item.date | defaultDateTimeFormatter }}
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-card>
              </v-flex>
            </v-layout>

            <v-layout class="caption grey--text text--darken-1">
              <v-flex shrink>
                BGC
                <span v-for="(stat, idx) in bgcDatasets" :key="idx">
                  <span>
                    {{
                      stat.direction === 0
                        ? $t('@statisticsPage/lastSent')
                        : $t('@statisticsPage/lastReceived')
                    }}&nbsp;
                  </span>
                  <span>{{ stat.date | defaultDateTimeFormatter }}.&nbsp;</span>
                </span>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-expansion-panel-content>

      <v-expansion-panel-content>
        <template v-slot:header>
          <h3 class="pl-0 py-2">{{ $t('@statisticsPage/backgroundProcessesSection/title') }}</h3>
        </template>

        <v-card flat>
          <v-container wrap fluid grid-list-lg class="pt-0">
            <v-layout row align-center justify-center class="py-4" v-if="loading">
              <v-flex d-flex sm12 align-content-center justify-center>
                <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
              </v-flex>
            </v-layout>

            <v-layout
              row
              align-center
              justify-center
              class="py-4"
              v-if="!loading && !jobsList.length"
            >
              <v-flex d-flex sm12 align-content-center justify-center>
                {{ $t('@statisticsPage/backgroundProcessesSection/emptyListPlaceholder') }}
              </v-flex>
            </v-layout>

            <v-layout row wrap v-if="!loading && jobsList.length">
              <v-flex xs12 sm6 md4 lg3 v-for="item in jobsList" :key="item.meta.jobId">
                <v-card color="grey lighten-3" flat tile>
                  <v-card-title class="pa-2">
                    <v-flex grow>
                      <h4>{{ item.meta.jobName }}</h4>
                    </v-flex>
                    <v-flex shrink>{{ item.meta.progress || 0 }}%</v-flex>
                  </v-card-title>

                  <v-progress-linear
                    color="success"
                    height="2"
                    :value="item.meta.progress || 0"
                    class="pa-0 ma-0"
                  ></v-progress-linear>

                  <v-list dense class="transparent">
                    <v-list-tile>
                      <v-list-tile-content>
                        {{
                          $t(
                            '@statisticsPage/backgroundProcessesSection/processCardDetails/durationLabel',
                          )
                        }}:
                      </v-list-tile-content>
                      <v-list-tile-content class="align-end">
                        {{ item.meta.minutes }}
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        {{
                          $t(
                            '@statisticsPage/backgroundProcessesSection/processCardDetails/stepLabel',
                          )
                        }}:
                      </v-list-tile-content>
                      <v-list-tile-content class="align-end">
                        {{ item.message || item.meta.stepName }}
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        {{
                          $t(
                            '@statisticsPage/backgroundProcessesSection/processCardDetails/updatedLabel',
                          )
                        }}:
                      </v-list-tile-content>
                      <v-list-tile-content class="align-end">
                        {{ item.createdAt | defaultDateTimeFormatter }}
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-card>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </app-page>
</template>

<style scoped>
.v-expansion-panel__header h3 {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.74);
}

.v-card--flat.v-sheet--tile {
  border: 1px solid rgba(0, 0, 0, 0.02) !important;
}
</style>

<script>
import { defaultDateTimeFormatter } from '../../utils';
import { getErrorMessage } from '../../errorHandlers';
import { VLayout } from 'vuetify/lib';
import ErrorsList from '../../components/ErrorsList';

export default {
  components: {
    VLayout,
    ErrorsList,
  },

  filters: {
    defaultDateTimeFormatter,
    formatNumber(val) {
      const n = Number(val);
      return isNaN(n) ? '-' : n.toLocaleString('en');
    },
  },

  data() {
    return {
      loading: false,
      jobs: {},
      errorMessage: '',

      statLoading: false,
      statItems: [],
      bgcDatasets: {},
      panel: [true, true],
    };
  },

  computed: {
    jobsList() {
      return Object.values(this.jobs).sort(
        (a, b) => parseFloat(a.meta.progress) - parseFloat(b.meta.progress),
      );
    },
  },

  methods: {
    async fetchData() {
      // Fetch billing stats
      try {
        this.statLoading = true;
        const { data: statsData } = await this.$http.get(`/api/stat/withdrawal`);
        this.statItems = statsData.cards;
        this.bgcDatasets = statsData.bgcDatasets;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.statLoading = false;
      }

      // Fetch background job stats
      try {
        this.loading = true;
        const { data } = await this.$http.get(`/api/stat/jobs`);
        this.jobs = data;

        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },

  sockets: {
    insuranceSubscribe: function(data) {
      if (data.meta && data.meta.isBackgroundReport) {
        const key = data.meta.jobName || data.type;
        this.jobs = { ...this.jobs, [key]: data };
      }
    },
  },

  async mounted() {
    await this.fetchData();
  },
};
</script>
<style scoped>
.v-list__tile__content.align-end {
  align-items: flex-end;
}
</style>
